import React, { ReactElement, ReactNode, useEffect, useState } from 'react';
import {
  Button,
  ConfigProvider,
  Flex,
  theme,
  Typography,
} from '@duxton-web/ui';
import { Select, TablePaginationConfig } from 'antd';
import { OptionProps } from 'antd/lib/select';

import HashEditInput from '../../common/HashEditInput';
import Service, { Facet, Group } from '../../lib/App';
import GroupList from './GroupList';

import './GroupPage.css';

import _ from 'lodash';
import { useLocation } from 'react-router-dom';

import {
  AccessControlWrapper,
  ObjectDetailPage,
} from '../../common/components';
import { ANNOUNCEMENTS } from '../MainLayout/announcements';
import GroupCreate from './GroupCreate';
import { GroupType } from '@/common/const';
import EnvironmentToggle from '@/components/environment-toggle';
import Announcements from '@/features/announcement';
import { UserAction } from '@/utils/accessControl';

// The number of rows to show
const LIMIT = 20;

// The initial offset to retrieve from api
const OFFSET = 0;

// Data retreived will be sorted by this
const SORTBY = 'name';

// Allow search option for dropdown
const isSearchAllowed = (
  search: string,
  node: React.ReactElement<OptionProps>,
): boolean =>
  String(node.key).toLowerCase().includes(search.toLowerCase()) ||
  String(node.props.children).toLowerCase().includes(search.toLowerCase());

const GroupPage = () => {
  const [showCreationDialog, setShowCreationDialog] = useState<boolean>(false);
  const [groups, setGroups] = useState<Group[]>([]);
  const [facet, setFacet] = useState<string>('');
  const [currentItem, setCurrentItem] = useState<Group>(null);
  const [error, setError] = useState<string>('');
  const [totalCount, setTotalCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isFetchingGroups, setIsFetchingGroups] = useState<boolean>(false);
  const facets: any[] = [
    { name: '', title: 'None' },
    ...Service.cache!.getAllFacets(),
  ];
  const location = useLocation();

  async function getGroup(
    searchTerm: string,
    offset: number,
    facet: string,
    includeTotalCount: boolean,
  ): Promise<any> {
    return await Service.planner.fetchGroupsV2(
      searchTerm,
      LIMIT,
      offset,
      SORTBY,
      facet,
      GroupType.STATIC,
      includeTotalCount,
    );
  }

  async function handleFilterChange(facet: string): Promise<void> {
    const searchQuery = location.hash.replace('#', '');
    setIsFetchingGroups(true);
    const response = await getGroup(searchQuery, OFFSET, facet, true);

    setFacet(facet);
    setIsFetchingGroups(false);
    setCurrentPage(1);
    setGroups(response.items);
    setTotalCount(response.totalCount);
  }

  async function handlePageChange(
    pagination: TablePaginationConfig,
  ): Promise<void> {
    setIsFetchingGroups(true);
    const offset = (pagination.current - 1) * LIMIT;
    const searchQuery = location.hash.replace('#', '');
    const groups = await getGroup(searchQuery, offset, facet, false);

    setCurrentPage(pagination.current);
    setIsFetchingGroups(false);
    setGroups(groups.items);
  }

  async function fetchGroup() {
    setIsFetchingGroups(true);
    const searchQuery = location.hash.replace('#', '');
    const response = await getGroup(searchQuery, OFFSET, facet, true);

    setIsFetchingGroups(false);
    setGroups(response.items);
    setTotalCount(response.totalCount);
  }

  const handleOnSearch = _.debounce(async (searchQuery: string) => {
    const response = await getGroup(searchQuery, OFFSET, facet, true);
    setGroups(response.items);
    setTotalCount(response.totalCount);
  }, 450);

  useEffect(() => {
    fetchGroup();
  }, []);

  async function editGroup(group: Group) {
    setIsFetchingGroups(true);

    let completeGroup: Group;

    try {
      completeGroup = await Service.planner!.fetchGroup(group.name);
      setCurrentItem(completeGroup);
      setShowCreationDialog(true);
      setIsFetchingGroups(false);
      setError('');
    } catch (e) {
      setShowCreationDialog(false);
      setIsFetchingGroups(false);
      setError(`${e.message}. Group data is too huge to load.`);
    }
  }

  function createGroup(): void {
    setCurrentItem(null);
    setShowCreationDialog(true);
  }

  function renderGroupModal(): ReactNode {
    if (!showCreationDialog) return false;

    return (
      <GroupCreate
        item={currentItem}
        onCancel={() => setShowCreationDialog(false)}
      />
    );
  }

  function renderMainContent(): ReactNode {
    const pagination: TablePaginationConfig = {
      total: totalCount,
      pageSize: LIMIT,
      current: currentPage,
      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} groups`,
      showSizeChanger: false,
      style: { alignItems: 'center' },
    };

    return (
      <Flex
        vertical
        gap={16}
        style={{
          height: '100%',
          padding: 24,
        }}
      >
        <Announcements id="grabx-announcements" items={ANNOUNCEMENTS} />
        <Flex align="center" justify="space-between">
          <Flex gap={8} align="center">
            <ConfigProvider
              theme={{
                components: {
                  Typography: {
                    titleMarginBottom: 0,
                  },
                  Segmented: {
                    trackBg: theme.getDesignToken().white,
                    motionDurationFast: '0',
                  },
                },
              }}
            >
              <Typography.Title level={3}>Groups</Typography.Title>

              <EnvironmentToggle />
            </ConfigProvider>
          </Flex>
        </Flex>
        <Flex vertical style={{ overflow: 'hidden' }}>
          <div className="group-page__header">
            <div className="group-page__filters-container">
              <div className="group-page__filters">
                <HashEditInput
                  placeholder="Search by group name"
                  onSearch={handleOnSearch}
                  disabled={isFetchingGroups}
                />

                <div className="group-filters__filter-by">
                  <p>Facet:</p>
                  <Select
                    style={{ width: '200px', marginLeft: '8px' }}
                    value={facet}
                    onChange={handleFilterChange}
                    filterOption={isSearchAllowed}
                    showSearch
                  >
                    {facets.map((f: Facet) => (
                      <Select.Option key={f.name} value={f.name}>
                        {f.title}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </div>
              <AccessControlWrapper action={UserAction.CREATE_GROUP}>
                {(hasAccess) => (
                  <div className="group-page__icon-container">
                    <Button
                      type="primary"
                      size="small"
                      onClick={createGroup}
                      disabled={!hasAccess}
                    >
                      Create Group
                    </Button>
                  </div>
                )}
              </AccessControlWrapper>
            </div>
          </div>

          <GroupList
            items={groups}
            onEdit={editGroup}
            error={error}
            onPageChange={handlePageChange}
            isLoading={isFetchingGroups}
            pagination={pagination}
          />
        </Flex>
      </Flex>
    );
  }

  function renderGroupPage(): ReactElement {
    return (
      <ObjectDetailPage>
        {renderMainContent()}
        {renderGroupModal()}
      </ObjectDetailPage>
    );
  }

  return renderGroupPage();
};

export default GroupPage;
